import { useEffect, useMemo, useRef, useState } from "react";
import { endpoints } from "@/lib/api";
import { INSTAGRAM_FALLBACK } from "@/lib/localMedia";
import {
  Play,
  Images,
  ChevronLeft,
  ChevronRight,
  Sparkles,
} from "lucide-react";

const PROFILE_META = {
  "instituto.ga": { label: "@instituto.ga", accent: "bg-ga-emerald" },
  "enaga.ga": { label: "@enaga.ga", accent: "bg-blue-500" },
  "futebolclubepe": { label: "@futebolclubepe", accent: "bg-ga-ink" },
  "missaomaiss": { label: "@missaomaiss", accent: "bg-ga-amber" },
};

function PostCard({ post }) {
  const isVideo = post.media_type === "video";
  const isCarousel = post.media_type === "carousel";

  return (
    <a
      href={post.permalink || "#"}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`ig-card-${post.id}`}
      className="group relative shrink-0 snap-start block w-[70%] xs:w-[55%] sm:w-[40%] md:w-[30%] lg:w-[22%] aspect-[4/5] rounded-2xl overflow-hidden bg-ga-ink shadow-sm transition-transform duration-500 hover:-translate-y-1"
    >
      <img
        src={post.thumbnail_url || post.media_url}
        alt={post.caption || "Post do Instituto"}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ga-ink via-ga-ink/30 to-transparent" />

      {/* Top row: media indicator (sem label de Instagram) */}
      <div className="absolute top-3 left-3 right-3 flex items-start justify-end gap-2">
        {isVideo && (
          <span className="bg-white/15 backdrop-blur text-white p-1.5 rounded-full" aria-label="Vídeo">
            <Play className="w-3 h-3 fill-white" />
          </span>
        )}
        {isCarousel && (
          <span className="bg-white/15 backdrop-blur text-white p-1.5 rounded-full" aria-label="Carrossel">
            <Images className="w-3 h-3" />
          </span>
        )}
      </div>

      {/* Play button center for videos */}
      {isVideo && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-12 h-12 rounded-full bg-white/25 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <Play className="w-5 h-5 fill-white text-white translate-x-[1px]" />
          </div>
        </div>
      )}

      {/* Caption fixed at bottom */}
      <div className="absolute inset-x-0 bottom-0 p-4">
        <p className="text-white text-[13px] leading-snug line-clamp-2 font-medium">
          {post.caption}
        </p>
      </div>
    </a>
  );
}

export default function InstagramHub() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usingFallback, setUsingFallback] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    endpoints
      .feed({ limit: 8 })
      .then((data) => {
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          setPosts(data.slice(0, 8));
          setUsingFallback(false);
        } else {
          setPosts(INSTAGRAM_FALLBACK);
          setUsingFallback(true);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setPosts(INSTAGRAM_FALLBACK);
        setUsingFallback(true);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  // Filtra para exibir somente imagens/álbuns — vídeos automáticos do Instagram
  // não aparecem aqui. Vídeos institucionais ficam na seção "Nossas Histórias".
  const displayPosts = useMemo(
    () => posts.filter((p) => p.media_type !== "video").slice(0, 8),
    [posts]
  );

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-testid^='ig-card-']");
    const step = card ? card.clientWidth + 16 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section
      id="instagram"
      data-testid="instagram-section"
      className="section-pad bg-background"
    >
      <div className="container-ga">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <div className="pill border-ga-ink/10 text-ga-ink/70 bg-ga-ink/5 mb-5" data-testid="instagram-pill">
              <Sparkles className="w-3.5 h-3.5" />
              Dia a dia do Instituto
            </div>
            <h2 className="font-display font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-ga-ink">
              Nossa realidade em <span className="gradient-text-ga">ação</span>.
            </h2>
            <p className="mt-4 text-ga-ink/60 text-base sm:text-lg">
              Aqui você vê o que realmente acontece todos os dias no Instituto.
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Desktop arrows */}
            <div className="hidden md:flex gap-2">
              <button
                type="button"
                onClick={() => scrollBy(-1)}
                data-testid="ig-arrow-left"
                aria-label="Anterior"
                className="w-11 h-11 rounded-full border-2 border-border text-ga-ink hover:border-ga-ink hover:bg-ga-ink hover:text-white transition-colors flex items-center justify-center"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => scrollBy(1)}
                data-testid="ig-arrow-right"
                aria-label="Próximo"
                className="w-11 h-11 rounded-full border-2 border-border text-ga-ink hover:border-ga-ink hover:bg-ga-ink hover:text-white transition-colors flex items-center justify-center"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex gap-4 overflow-hidden" data-testid="instagram-loading">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="shrink-0 w-[70%] sm:w-[40%] lg:w-[22%] aspect-[4/5] rounded-2xl bg-ga-ink/5 animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div
            ref={scrollerRef}
            className="-mx-2 px-2 flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-none scroll-smooth pb-3"
            data-testid="instagram-carousel"
          >
            {displayPosts.map((post) => (
              <PostCard key={post.id} post={post} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
